import { forwardRef, HTMLAttributes } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../utils/cn';

const loadingVariants = cva('animate-spin', {
  variants: {
    size: {
      sm: 'h-4 w-4',
      md: 'h-6 w-6',
      lg: 'h-8 w-8',
      xl: 'h-12 w-12',
    },
    variant: {
      spinner: '',
      dots: '',
      pulse: '',
    },
  },
  defaultVariants: {
    size: 'md',
    variant: 'spinner',
  },
});

export interface LoadingProps
  extends HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof loadingVariants> {
  text?: string;
}

const Spinner = ({ size, className }: { size?: string; className?: string }) => (
  <svg
    className={cn('animate-spin', size, className)}
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
  >
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    />
  </svg>
);

const Dots = ({ size, className }: { size?: string; className?: string }) => (
  <div className={cn('flex space-x-1', className)}>
    <div className={cn('bg-current rounded-full animate-pulse', size)} />
    <div className={cn('bg-current rounded-full animate-pulse', size)} style={{ animationDelay: '0.1s' }} />
    <div className={cn('bg-current rounded-full animate-pulse', size)} style={{ animationDelay: '0.2s' }} />
  </div>
);

const Pulse = ({ size, className }: { size?: string; className?: string }) => (
  <div className={cn('bg-current rounded-full animate-pulse', size, className)} />
);

const Loading = forwardRef<HTMLDivElement, LoadingProps>(
  ({ className, size, variant, text, ...props }, ref) => {
    const sizeClass = loadingVariants({ size });
    
    const renderLoadingElement = () => {
      switch (variant) {
        case 'dots':
          return <Dots size={sizeClass} className={className} />;
        case 'pulse':
          return <Pulse size={sizeClass} className={className} />;
        default:
          return <Spinner size={sizeClass} className={className} />;
      }
    };

    return (
      <div
        ref={ref}
        className={cn('flex items-center justify-center', className)}
        {...props}
      >
        <div className="flex flex-col items-center space-y-2">
          {renderLoadingElement()}
          {text && (
            <span className="text-sm text-gray-600 animate-pulse">{text}</span>
          )}
        </div>
      </div>
    );
  }
);

Loading.displayName = 'Loading';

// Individual loading components for specific use cases
const LoadingSpinner = ({ size = 'md', className }: { size?: 'sm' | 'md' | 'lg' | 'xl'; className?: string }) => (
  <Spinner size={loadingVariants({ size })} className={className} />
);

const LoadingButton = ({ children, loading, ...props }: any) => (
  <button disabled={loading} {...props}>
    <div className="flex items-center">
      {loading && <LoadingSpinner size="sm" className="mr-2" />}
      {children}
    </div>
  </button>
);

const LoadingOverlay = ({ loading, children, text }: { loading: boolean; children: React.ReactNode; text?: string }) => (
  <div className="relative">
    {children}
    {loading && (
      <div className="absolute inset-0 bg-white/80 dark:bg-gray-900/80 flex items-center justify-center z-50">
        <Loading variant="spinner" size="lg" text={text} />
      </div>
    )}
  </div>
);

export { 
  Loading, 
  LoadingSpinner, 
  LoadingButton, 
  LoadingOverlay, 
  loadingVariants 
};
